import React, { useState } from 'react';
import {
  Button,
  IconButton,
  Input,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';

import Dialog from '../../ui/Dialog';
import LoadingOverlay from '../../LoadingOverlay/LoadingOverlay.component';
import { markCycleAsRedeemed } from '../../../services/transaction.service';
import { upload } from '../../../services/storage.service';
import moment from 'moment';

const MarkAsRedeemed = ({ data }) => {
  const { program_id, transaction_code_id, transaction_type } = data;

  const [state, setState] = useState({
    openConfirmation: false,
    openMarkAsRedeemedConfirmation: true,
    modalTitle: 'Mark As Redeemed',
    errorMessage: null,
    successMessage: 'Cycle has been marked as redeemed successfully!',
    isLoading: false,
    latestMessage: '',
    messageLogId: null,
    submitButtonEnabled: false,
  });

  const [redemptionDetails, setRedemptionDetails] = useState({
    quantity: 1,
    date: '',
  });

  const [purchaseProducts, setPurchaseProducts] = useState([{
    cycle: '',
    quantity: 1,
    receiptDate: '',
    receiptImage: '',
  }]);

  const handleRowChange = (index, field, value) => {
    setPurchaseProducts(prev => prev.map((row, i) => i === index ? { ...row, [field]: value } : row));
  };

  const handleDuplicateRow = (index) => {
    setPurchaseProducts(prev => [...prev, { ...prev[index] }]);
  };

  const handleDeleteRow = (index) => {
    setPurchaseProducts(prev => prev.filter((_, i) => i !== index));
  };

  const handleClose = () => {
    const { openConfirmation } = state;
    setState(prev => ({
      ...prev,
      openConfirmation: false,
      openMarkAsRedeemedConfirmation: false,
      errorMessage: null
    }));

    if (openConfirmation) {
      window.location.reload();
    }
  };

  const onValidate = async () => {
    setState(prev => ({ ...prev, isLoading: true }));

    try {
      const response = await markCycleAsRedeemed({
        program_id,
        transaction_code_id,
        payload: {
          ...(purchaseProducts.length > 0 && { products: purchaseProducts.map(product => ({
            cycle: product.cycle,
            quantity: parseInt(product.quantity.toString()),
            receipt_date: product.receiptDate,
            receipt_image: product.receiptImage,
          })) }),
          ...(redemptionDetails.date && { created_at: redemptionDetails.date}),
          ...(redemptionDetails.quantity && { quantity: parseInt(redemptionDetails.quantity.toString())}),
        },
      });
      
      setState(prev => ({
        ...prev,
        openValidateConfirmation: false,
        successMessage: response.message,
      }));
    } catch (e) {
      setState(prev => ({
        ...prev,
        errorMessage: e.message
      }));
    } finally {
      setState(prev => ({ ...prev, isLoading: false, openMarkAsRedeemedConfirmation: false, openConfirmation: true }));
    }
  };

  const uploadReceiptImage = async (index: number, file: File) => {
    if (!file) return;

    const { result } = await upload(blobToFile(file), program_id);

    setPurchaseProducts(prev => prev.map((row, i) => i === index ? { ...row, receiptImage: result.url } : row));
  };

  const blobToFile = (imageBlob) => {
    return new File([imageBlob], imageBlob.name, {
        lastModified: moment().unix(),
        type: imageBlob.type,
    });
  }

  const {
    openConfirmation,
    modalTitle,
    errorMessage,
    successMessage,
    isLoading,
    openMarkAsRedeemedConfirmation
  } = state;

  if (isLoading) return <LoadingOverlay />;

  const formButtons = (
    <>
      <Button onClick={handleClose} variant="contained">
        {openConfirmation ? 'OK' : 'Close'}
      </Button>
      {
        openMarkAsRedeemedConfirmation && 
        <Button
          onClick={onValidate}
          variant="contained"
          color="primary"
          disabled={false}
        >
          Mark As Redeemed
        </Button>
      }
    </>
  );

  const redemptionContent = (
    <>
    { transaction_type === 'free_good' && (
      <TableContainer component={Paper} key="free-good-details">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Quantity*</TableCell>
                <TableCell>Redemption Date*</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
                <TableRow>
                  <TableCell>
                    <Input
                      type="number"
                      fullWidth
                      value={redemptionDetails.quantity}
                      onChange={(e: any) => setRedemptionDetails(prev => ({ ...prev, quantity: e.target.value }))}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      type="date"
                      fullWidth
                      value={redemptionDetails.date}
                      onChange={(e: any) => setRedemptionDetails(prev => ({ ...prev, date: e.target.value }))}
                      InputLabelProps={{ shrink: true }}
                    />
                  </TableCell>
                </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      )
    }
    { transaction_type === 'purchase' && (
      <TableContainer component={Paper} key="purchase-details">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell style={{ width: '50px' }}>Cycle Number*</TableCell>
                <TableCell style={{ width: '50px' }}>Quantity*</TableCell>
                <TableCell>Receipt Date*</TableCell>
                <TableCell style={{ width: '300px' }}>Receipt Image*</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {purchaseProducts.map((row, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <Input
                      type="text"
                      fullWidth
                      value={row.cycle}
                      onChange={(e) => handleRowChange(index, 'cycle', e.target.value)}
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      type="number"
                      fullWidth
                      value={row.quantity}
                      onChange={(e) => handleRowChange(index, 'quantity', e.target.value)}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      type="date"
                      fullWidth
                      value={row.receiptDate}
                      onChange={(e) => handleRowChange(index, 'receiptDate', e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      type="file"
                    fullWidth
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => uploadReceiptImage(index, e.target.files ? e.target.files[0] : null)}
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton color="primary" onClick={() => handleDuplicateRow(index)}>
                      <AddIcon />
                    </IconButton>
                    <IconButton color="primary" onClick={() => handleDeleteRow(index)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )
    }
    </>
  )

  return (
    <>
      <Dialog
        key="mark-as-redeemed"
        open={openMarkAsRedeemedConfirmation}
        title={modalTitle}
        content = {redemptionContent}
        children={formButtons}
        onSubmit={onValidate}
        onClose={handleClose}
      />
      <Dialog 
        onSubmit={handleClose}
        key="confirmation"
        open={openConfirmation}
        title='Mark As Redeemed'
        content={errorMessage || successMessage}
        children={formButtons}
        onClose={handleClose}
      />
      <Button variant="contained" color="primary" onClick={() => {
         setState(prev => ({
          ...prev,
          openMarkAsRedeemedConfirmation: true,
        }));
      }} style={{ marginRight: 8 }}>
        Mark As Redeemed
      </Button>
    </>
  );
};

export default MarkAsRedeemed;